/**
 * Delivery Location Templates Management Hook
 * 
 * Manages reusable delivery location templates for quick job setup. Templates
 * store complete delivery address and requirements information, eliminating
 * repetitive data entry for frequently-used locations.
 * 
 * **Template Structure:**
 * - Location identification (name, type)
 * - Complete address information
 * - Contact person details
 * - Delivery requirements and restrictions
 * - Access instructions
 * - Preferred/restricted carriers
 * - Dock and loading information
 * - Geocoding for distance calculations
 * 
 * **Location Types:**
 * - warehouse: Distribution centers, warehouses
 * - office: Office buildings
 * - retail: Retail stores, showrooms
 * - manufacturing: Production facilities
 * - residential: Home addresses
 * - construction_site: Active construction sites
 * - other: Custom location types
 * 
 * **Usage Tracking:**
 * - usage_count incremented each time template used
 * - Templates sorted by popularity (most used first)
 * - Helps identify frequently-used locations
 * - Informs template management decisions
 * 
 * **Key Features:**
 * - Create, update, clone templates
 * - Filter by location type and organization
 * - Search by name, city, or state
 * - Usage count tracking
 * - Soft delete via deactivation
 * - Geocoding integration
 * 
 * **Use Cases:**
 * - Quick job creation with saved locations
 * - Multi-location delivery setup
 * - Organization-wide location library
 * - Recurring delivery address management
 * - Template-based job creation
 * 
 * @param options - Optional filters for templates
 * @returns {Object} Template management functions and data
 * @returns {DeliveryLocationTemplate[]} templates - Array of templates
 * @returns {boolean} loading - Loading state
 * @returns {string|null} error - Error message if any
 * @returns {Function} refetch - Manually refresh templates
 * @returns {Function} createTemplate - Create new template
 * @returns {Function} updateTemplate - Update existing template
 * @returns {Function} cloneTemplate - Duplicate template with new name
 * @returns {Function} incrementUsageCount - Track template usage
 * @returns {Function} deactivateTemplate - Soft delete template
 * 
 * @example
 * // Load and use templates for job creation
 * const { templates, loading, incrementUsageCount } = useDeliveryTemplates({
 *   organizationId: myOrgId,
 *   locationType: 'warehouse'
 * });
 * 
 * const handleSelectTemplate = async (template: DeliveryLocationTemplate) => {
 *   await incrementUsageCount(template.id);
 *   setDeliveryLocation(template);
 * };
 * 
 * @example
 * // Create new delivery template
 * const { createTemplate } = useDeliveryTemplates();
 * 
 * const handleSaveTemplate = async () => {
 *   await createTemplate({
 *     location_name: 'Main Distribution Center',
 *     location_type: 'warehouse',
 *     address_line1: '1234 Industrial Way',
 *     city: 'Portland',
 *     state_province: 'OR',
 *     postal_code: '97220',
 *     country: 'USA',
 *     contact_name: 'John Doe',
 *     contact_phone: '555-0100',
 *     delivery_hours: { mon_fri: '8:00 AM - 4:00 PM', sat: 'Closed' },
 *     dock_information: 'Bay 3-5 available for receiving'
 *   });
 * };
 * 
 * @example
 * // Clone existing template for similar location
 * const { cloneTemplate } = useDeliveryTemplates();
 * 
 * const handleClone = async (templateId: string) => {
 *   const newTemplate = await cloneTemplate(
 *     templateId,
 *     'Secondary Distribution Center'
 *   );
 *   // Edit the cloned template as needed
 *   navigate(`/templates/${newTemplate.id}/edit`);
 * };
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { DeliveryLocationTemplate, LocationType } from '@/lib/types/job';

interface UseDeliveryTemplatesOptions {
  locationType?: LocationType;
  search?: string;
  organizationId?: string;
}

export const useDeliveryTemplates = (options: UseDeliveryTemplatesOptions = {}) => {
  const [templates, setTemplates] = useState<DeliveryLocationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('delivery_location_templates')
        .select('*')
        .eq('is_active', true)
        .order('usage_count', { ascending: false });

      if (options.organizationId) {
        query = query.eq('organization_id', options.organizationId);
      }

      if (options.locationType) {
        query = query.eq('location_type', options.locationType);
      }

      if (options.search) {
        query = query.textSearch('location_name,city,state_province', options.search);
      }

      const { data, error } = await query;

      if (error) throw error;

      setTemplates((data || []) as DeliveryLocationTemplate[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching delivery templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [options.locationType, options.search, options.organizationId]);

  const createTemplate = async (templateData: Partial<DeliveryLocationTemplate>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      const { data, error } = await supabase
        .from('delivery_location_templates')
        .insert({
          ...templateData,
          organization_id: profile.organization_id,
          created_by_user_id: user.id,
          last_updated_by_user_id: user.id,
        } as any)
        .select()
        .single();

      if (error) throw error;

      setTemplates([data as DeliveryLocationTemplate, ...templates]);
      
      toast({
        title: 'Template created',
        description: `Delivery template "${data.location_name}" has been created`,
      });

      return data;
    } catch (err) {
      console.error('Error creating template:', err);
      toast({
        title: 'Error creating template',
        description: err instanceof Error ? err.message : 'Failed to create template',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const updateTemplate = async (id: string, updates: Partial<DeliveryLocationTemplate>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('delivery_location_templates')
        .update({
          ...updates,
          last_updated_by_user_id: user.id,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setTemplates(templates.map(t => t.id === id ? data as DeliveryLocationTemplate : t));
      
      toast({
        title: 'Template updated',
        description: `Template "${data.location_name}" has been updated`,
      });

      return data;
    } catch (err) {
      console.error('Error updating template:', err);
      toast({
        title: 'Error updating template',
        description: err instanceof Error ? err.message : 'Failed to update template',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const cloneTemplate = async (templateId: string, newName: string) => {
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) throw new Error('Template not found');

      const clonedTemplate = await createTemplate({
        location_name: newName,
        location_type: template.location_type,
        address_line1: template.address_line1,
        address_line2: template.address_line2,
        city: template.city,
        state_province: template.state_province,
        postal_code: template.postal_code,
        country: template.country,
        contact_name: template.contact_name,
        contact_phone: template.contact_phone,
        contact_email: template.contact_email,
        special_instructions: template.special_instructions,
        access_requirements: template.access_requirements,
        delivery_hours: template.delivery_hours,
        preferred_carriers: template.preferred_carriers,
        restricted_carriers: template.restricted_carriers,
        dock_information: template.dock_information,
      });

      return clonedTemplate;
    } catch (err) {
      console.error('Error cloning template:', err);
      throw err;
    }
  };

  const incrementUsageCount = async (templateId: string) => {
    try {
      const { data } = await supabase
        .from('delivery_location_templates')
        .select('usage_count')
        .eq('id', templateId)
        .single();
      
      if (data) {
        await supabase
          .from('delivery_location_templates')
          .update({ usage_count: (data.usage_count || 0) + 1 })
          .eq('id', templateId);
      }
    } catch (err) {
      console.error('Error incrementing usage count:', err);
    }
  };

  const deactivateTemplate = async (id: string) => {
    try {
      await supabase
        .from('delivery_location_templates')
        .update({ is_active: false })
        .eq('id', id);

      setTemplates(templates.filter(t => t.id !== id));
      
      toast({
        title: 'Template deactivated',
        description: 'Template has been deactivated and removed from the active list',
      });
    } catch (err) {
      console.error('Error deactivating template:', err);
      toast({
        title: 'Error deactivating template',
        description: err instanceof Error ? err.message : 'Failed to deactivate template',
        variant: 'destructive',
      });
      throw err;
    }
  };

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates,
    createTemplate,
    updateTemplate,
    cloneTemplate,
    incrementUsageCount,
    deactivateTemplate,
  };
};
